import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { CtaBand } from "../components/CtaBand";
import { IMAGES } from "../data/site";

const CHAPTERS = [
  {
    num: "01",
    title: "Operations first, always",
    text: "Alexandre's career was built where races are actually won or lost: logistics, timing, preparation, and the thousand details that decide whether a weekend runs clean.",
  },
  {
    num: "02",
    title: "The driver is the point",
    text: "Every process at ABS LLC exists to protect the driver's focus. When the operation disappears into the background, performance moves to the foreground.",
  },
  {
    num: "03",
    title: "Positive, reliable, turnkey",
    text: "Clients stay because the experience is calm and the execution is exact. Problems are solved before they reach the cockpit — that is the entire philosophy.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        overline="About ABS LLC"
        lines={["Three decades", "of making race", "weekends work."]}
        description="Alex Brosseau Services LLC is a U.S.-based motorsport concierge founded on a simple conviction: amateur and semi-professional drivers deserve a professional-grade operation behind them."
        image={IMAGES.teamFamily}
        imageAlt="The Alex Brosseau Racing team and families at the circuit"
      />

      <section data-testid="about-founder" className="border-t border-neutral-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 py-20 md:grid-cols-12 md:px-8 md:py-28">
          <div className="md:col-span-5">
            <Reveal>
              <p className="font-overline text-[10px] uppercase tracking-[0.35em] text-[#e63946] md:text-[11px]">
                The founder
              </p>
              <h2 className="font-display mt-5 text-4xl font-extrabold tracking-tight text-[#111111] sm:text-5xl">
                Alexandre
                <br />
                Brosseau
              </h2>
              <p className="mt-4 font-overline text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                Founder & Principal — 30+ years in motorsport
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <blockquote className="mt-10 border-l-2 border-[#e63946] pl-6">
                <p className="text-lg leading-relaxed text-neutral-700 md:text-xl">
                  "A driver should spend a race weekend thinking about braking
                  points — not about whether the car, the tires, or the truck
                  made it to the circuit. My job is to make sure they never
                  have to."
                </p>
              </blockquote>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="clip-corner img-treatment mt-10 overflow-hidden">
                <img
                  src={IMAGES.crewChief}
                  alt="Alexandre Brosseau trackside in the Watkins Glen paddock"
                  className="aspect-[4/3] w-full object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal delay={0.1}>
              <p className="text-base leading-relaxed text-neutral-600 md:text-lg">
                Alexandre has spent more than thirty years inside professional
                motorsport — running race operations, coordinating complex
                logistics across borders and seasons, and developing drivers
                from their first track day to the sharp end of competitive
                grids.
              </p>
              <p className="mt-6 text-base leading-relaxed text-neutral-600 md:text-lg">
                That experience now runs through every ABS LLC program: arrive-and-drive
                race weekends planned to the hour, transport and paddock
                operations executed without drama, and driver development built
                on honest data and honest conversation. The standard is
                professional, whatever the class or budget.
              </p>
            </Reveal>
            <div className="mt-12">
              {CHAPTERS.map((ch, i) => (
                <Reveal key={ch.num} delay={0.05 * i}>
                  <div className="grid gap-3 border-t border-neutral-200 py-8 sm:grid-cols-12">
                    <p className="font-overline text-sm tracking-[0.2em] text-neutral-400 sm:col-span-2">{ch.num}</p>
                    <h3 className="font-display text-lg font-bold tracking-tight text-[#111111] sm:col-span-4 md:text-xl">
                      {ch.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-neutral-600 sm:col-span-6">{ch.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CtaBand title="Work with experience." accent="Thirty years deep." />
    </div>
  );
}
